import { LogUserActivity } from '@/app/apis/users/requests'
import PageLoader from '@/app/common/components/PageLoader'
import { LOG_TYPES } from '@/app/common/constants'
import SimpleNavTabs, { Tab } from '@/common/components/SimpleNavTabs'
import { ProviderQueries } from '@/queries/providers/queries'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, useParams } from 'react-router-dom'

export default function ProviderDetailsPage() {
  const { providerId } = useParams<{ providerId: string }>()
  const programId = useSelector(selectSelectedProgram).id

  if (!providerId) {
    throw new Error('ProviderId Not found')
  }

  const providerQuery = useQuery(
    ProviderQueries.getProviderById(programId, providerId)
  )

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Provider review page',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  const Paths = AppRoutePaths.providers.provider_details

  // Helper function to replace provider parameters in paths
  const getProviderPath = (path: string) => {
    return path.replace(':providerId', providerId || '')
  }

  return (
    <>
      <PageLoader query={providerQuery}>
        {(data) => {
          const IS_NOT_ACCEPTED_PROVIDER =
            data.global_application_status_id !== 4

          const IS_SCHOOL_FORM = typeof data.school_application !== 'undefined'

          const Tabs: Tab[] = [
            {
              name: 'Provider Info',
              href: getProviderPath(Paths.provider_info),
              privilege: 'programs_read',
              isDisabled: false,
            },
            {
              name: 'Transactions',
              href: getProviderPath(Paths.transactions),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'Store',
              href: getProviderPath(Paths.marketplace),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'Add Product',
              href: getProviderPath(Paths.add_product),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'Manage Users',
              href: getProviderPath(Paths.manage_users),
              privilege: 'programs_read' as const,
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'Documents',
              href: getProviderPath(Paths.documents),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'School Information',
              href: getProviderPath(Paths.school_form),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER || !IS_SCHOOL_FORM,
            },
            {
              name: 'History',
              href: getProviderPath(Paths.history),
              privilege: 'programs_read',
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
            {
              name: 'Store Configuration',
              href: getProviderPath(Paths.store_config),
              isDisabled: IS_NOT_ACCEPTED_PROVIDER,
            },
          ]
          return (
            <div className="flex flex-col">
              <SimpleNavTabs tabs={Tabs} />
              <Outlet />
            </div>
          )
        }}
      </PageLoader>
    </>
  )
}
